import React, { useState, useEffect, useCallback } from "react";

import { Stack, Typography, Button, CircularProgress, Alert, Box } from "@mui/material";

import { message } from "antd";

import { useAuth } from "../../contexts/auth-context";
import Logo from "../../Images/Logo.svg";

// Google icon component
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M17.64 9.20455C17.64 8.56636 17.5827 7.95273 17.4764 7.36364H9V10.845H13.8436C13.635 11.97 13.0009 12.9232 12.0477 13.5614V15.8195H14.9564C16.6582 14.2527 17.64 11.9455 17.64 9.20455Z"
        fill="#4285F4"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5614C11.2418 14.1014 10.2109 14.4205 9 14.4205C6.65591 14.4205 4.67182 12.8373 3.96409 10.71H0.957275V13.0418C2.43818 15.9832 5.48182 18 9 18Z"
        fill="#34A853"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957273C0.347727 6.17318 0 7.54773 0 9C0 10.4523 0.347727 11.8268 0.957273 13.0418L3.96409 10.71Z"
        fill="#FBBC05"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z"
        fill="#EA4335"
      />
    </svg>
  );
}

// Clean up OAuth-related URL parameters
const cleanupOAuthUrlParams = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete("code");
  url.searchParams.delete("error");
  url.searchParams.delete("error_code");
  url.searchParams.delete("state");
  window.history.replaceState({}, document.title, url.toString());
};

function LoginPage() {
  const { bifrost, login } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ssoMetadata, setSsoMetadata] = useState(null);

  // Parse URL parameters
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const urlError = params.get("error");
  const errorCode = params.get("error_code");

  // Fetch SSO metadata on mount
  useEffect(() => {
    let isMounted = true;

    async function fetchSsoMetadata() {
      // Skip if we're processing a callback
      if (code) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await bifrost.getLoginMethods();
        const googleMethod = response.data.find((m) => m.id === "GOOGLE_SSO");

        if (isMounted && googleMethod?.meta_data) {
          setSsoMetadata({
            method_id: googleMethod.id,
            client_id: googleMethod.meta_data.client_id,
            redirect_uri: googleMethod.meta_data.redirect_uri
          });
        } else if (isMounted) {
          setError("Google SSO is not available. Please contact support.");
        }
      } catch (err) {
        if (isMounted) {
          console.error("Failed to fetch login methods:", err);
          setError("Failed to initialize authentication. Please refresh the page.");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    fetchSsoMetadata();

    return () => {
      isMounted = false;
    };
  }, [bifrost, code]);

  // Handle OAuth callback (code in URL)
  useEffect(() => {
    if (!code) return;

    let isMounted = true;

    async function exchangeCodeForTokens() {
      setIsLoading(true);
      setError(null);

      try {
        const response = await bifrost.exchangeCodeForTokens({
          sessionInfo: {
            platform: "WEB",
            loginMethod: "sso"
          },
          redirectUri: window.location.origin,
          code
        });

        if (response?.data?.access_token) {
          // Store tokens and authenticate, wait for user profile to be fetched
          await login(response.data.access_token, response.data.refresh_token, response.data.session_id);

          // Clean up URL
          cleanupOAuthUrlParams();

          // Redirect to home
          window.location.href = "/home";
        } else {
          throw new Error("No access token received");
        }
      } catch (err) {
        if (isMounted) {
          console.error("Token exchange failed:", err);
          setError("Authentication failed. Please try again.");
          cleanupOAuthUrlParams();
          setIsLoading(false);
        }
      }
    }

    exchangeCodeForTokens();

    return () => {
      isMounted = false;
    };
  }, [code, bifrost, login]);

  // Handle OAuth error (error in URL)
  useEffect(() => {
    if (!urlError) return;

    let errorMessage;
    if (urlError === "access_denied") {
      errorMessage = "Login was cancelled";
    } else if (errorCode === "AS_VE_03") {
      errorMessage = "Authentication error: Missing required parameter";
    } else {
      errorMessage = `Authentication error: ${urlError}`;
    }

    setError(errorMessage);
    message.error({ content: errorMessage, duration: 4 });
    cleanupOAuthUrlParams();
  }, [urlError, errorCode]);

  // Handle Google sign-in button click
  const handleGoogleSignIn = useCallback(async () => {
    if (!ssoMetadata) {
      message.error("SSO configuration not loaded. Please refresh the page.");
      return;
    }

    setIsButtonLoading(true);
    setError(null);

    try {
      await bifrost.sso({
        method_id: ssoMetadata.method_id,
        country_code: "AU",
        client_id: ssoMetadata.client_id,
        redirect_uri: ssoMetadata.redirect_uri
      });
      // Note: This will redirect to Google, so the component unmounts
    } catch (err) {
      console.error("SSO initiation failed:", err);
      setIsButtonLoading(false);
      setError("Failed to initiate Google sign-in. Please try again.");
      message.error("Failed to initiate Google sign-in");
    }
  }, [bifrost, ssoMetadata]);

  // Show loading spinner during callback processing
  if (isLoading && code) {
    return (
      <Stack direction="column" minHeight="100vh" alignItems="center" justifyContent="center" bgcolor="#F0F3F5">
        <CircularProgress size={48} />
        <Typography mt={2} variant="body1" color="text.secondary">
          Completing sign in...
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack direction="column" minHeight="100vh" alignItems="center" justifyContent="center" bgcolor="#F0F3F5">
      <Stack
        spacing={3}
        alignItems="center"
        sx={{
          bgcolor: "#ffffff",
          borderRadius: "12px",
          boxShadow: "0 4px 24px rgba(0, 34, 56, 0.1)",
          p: 5,
          minWidth: 400
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            mb: 1,
            bgcolor: "#1472b9"
          }}
        >
          <img src={Logo} alt="CARS24 Logo" style={{ height: 48 }} />
        </Box>

        {/* Title */}
        <Typography variant="h5" fontWeight={600} color="#002238" textAlign="center">
          Dealership Management System
        </Typography>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ width: "100%" }}>
            {error}
          </Alert>
        )}

        {/* Google Sign-in Button */}
        <Button
          variant="contained"
          onClick={handleGoogleSignIn}
          disabled={!ssoMetadata || isButtonLoading || isLoading}
          startIcon={!isButtonLoading && <GoogleIcon />}
          sx={{
            bgcolor: "#4285F4",
            "&:hover": { bgcolor: "#357ABD" },
            "&:disabled": { bgcolor: "#C8C8C8" },
            textTransform: "none",
            py: 1.5,
            px: 4,
            fontSize: "16px",
            fontWeight: 500,
            minWidth: 220
          }}
        >
          {isButtonLoading ? <CircularProgress size={24} color="inherit" /> : "Sign in with Google"}
        </Button>

        {/* Loading indicator for metadata fetch */}
        {isLoading && !code && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Loading...
            </Typography>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

export default LoginPage;
